import { AppBar, Autocomplete, Box, Button, FormControlLabel, IconButton, styled, Switch, TextField, Toolbar, Tooltip, Typography } from '@mui/material';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import TuneIcon from '@mui/icons-material/Tune';
import { useEffect, useMemo, useState } from 'react';
import PolicySimulator from '../../../utils/PolicySimulator.ts';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import CustomProfilesPolicySimulator from './components/CustomProfilesPolicySimulator.js';
import { getSimulatorProfiles, setSimulatorProfiles } from '../../../redux/simulatorProfilesSlice.js';
import { useDispatch, useSelector } from 'react-redux';
import findSubjectAttrToSimulator from '../../../utils/findSubjectAttrToSimulator.js';
import { getProfile } from '../../../redux/iaamSlice.js';
import { useNodes, useReactFlow } from '@xyflow/react';
import { setSnackbarMessage } from '../../../redux/snackbarMessageSlice.js';
import sortedProfilesAttributes from '../../../utils/sortedProfilesAttributes.js';
import UserProfileManagement from '../Regulatory/components/UserProfileManagement.js';
import ConfirmDialogReuse from '../ReuseComponents/ConfirmDialogReuse.js';
import { v4 as uuidv4 } from 'uuid';
import { dataTypes } from '../../../utils/configDataTypes.js';
import { getSubjectSchema } from '../../../redux/subjectSchemaSlice.js';
import { LoadingButton } from '@mui/lab';
import { getSelectedProfileSimulate, setSelectedProfileSimulate } from '../../../redux/selectedProfileSimulateSlice.js';
import DataGridPolicySimulator from './components/DataGridPolicySimulator.js';
import { getOpenSimulatorPolicy } from '../../../redux/openSimulatorPolicySlice.js';
import { getRuleByPolicyDiagram } from '../../../utils/convertPolicyStructure.js';
import dayjs from "dayjs";

const iaam = window.iaam;

const PolicySimulatorPanel = (props) => {
    const { onClose } = props;

    const dispatch = useDispatch();

    // const use_nodes = useNodes();
    const { getNodes, getEdges } = useReactFlow();
    const nodes = useNodes();

    const simulatorProfiles = useSelector(getSimulatorProfiles);
    const subjectSchema = useSelector(getSubjectSchema);
    const selectedProfileSimulate = useSelector(getSelectedProfileSimulate);
    const openSimulatorPolicy = useSelector(getOpenSimulatorPolicy);

    const [profile, setProfile] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [policySimulator, setPolicySimulator] = useState(null);
    const [isProfileChanged, setIsProfileChanged] = useState(false);
    const [project, setProject] = useState();
    const [selectedEditProfile, setSelectedEditProfile] = useState(null);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [selectedDeleteProfile, setSelectedDeleteProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openManagement, setOpenManagement] = useState(false);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        const file = JSON.parse(localStorage.getItem('policy-file'));
        const initialProfile = async () => {
            setProfile(await iaam.getProfile());
        }
        if (file) {
            setPolicySimulator(new PolicySimulator(file))
            initialProfile();
        }
        const project = JSON.parse(localStorage.getItem('project'));
        if (project) {
            setProject(project);
        }
    }, [])

    useEffect(() => {
        if (!nodes || nodes.length === 0) {
            return;
        }
        if (simulatorProfiles && simulatorProfiles.length > 0) {
            // console.log("simulatorProfiles", simulatorProfiles)
            const usedSubjectAttributes = findSubjectAttrToSimulator(nodes);
            const profilesData = simulatorProfiles.map(profile => {
                let attributes = {};
                Object.keys(usedSubjectAttributes).forEach(key => {
                    attributes[key] = profile?.attributes[key] || null;
                });
                return {
                    ...profile,
                    attributes: attributes,
                };
            });
            setProfiles(profilesData);
        }
    }, [simulatorProfiles, nodes]);

    const runSimulatePolicyTest = async (profile) => {
        // console.log("profile:", profile)

        //Case 1: Test WEB DDC-Care-Dashboard ไม่มีเงื่อนไข
        const policyRequest1 = policySimulator.createPolicyRequest(
            "e021af93-0b02-4e2e-b542-64137f341206",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
            },
            "open",
            {}, // ไม่มี environment, public_ip
        )

        //Case 2: Test WEB DDC-Care-Dashboard มีการกำหนดเวลา (ยังตรวจสอบไม่ถูกต้อง)
        const policyRequest2 = policySimulator.createPolicyRequest(
            "e021af93-0b02-4e2e-b542-64137f341206",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
            },
            "open",
            {
                timestamp: '2026-06-12 16:30:00'
            },
        )

        //Case 3: Test WEB DDC-Care-Dashboard มีการกำหนด IP address static
        const policyRequest3 = policySimulator.createPolicyRequest(
            "e021af93-0b02-4e2e-b542-64137f341206",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
            },
            "open",
            {
                // ip_address: "192.168.0.1", // IPv4 Static
                // ip_address: "192.168.0.*", // IPv4 Wildcard
                // ip_address: "192.168.0.0/24" // IPv4 CIDR/Subnet
                // ip_address: "192.168.0.1-192.168.0.9" // IPv4 Range
            },
            "192.168.3.9"
            // "190.160.0.16"
        )

        //Case 4: Test WEB DDC-Care-Dashboard มีการกำหนดเวลา และ IP address ด้วย
        const policyRequest4 = policySimulator.createPolicyRequest(
            "e021af93-0b02-4e2e-b542-64137f341206",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
            },
            "open",
            {
                timestamp: '2026-06-11 07:59:00',
            },
            "192.168.0.1"
        )

        //Case 5: Test API ddccare-api-workshop ไม่มีเงื่อนไข
        const policyRequest5 = policySimulator.createPolicyRequest(
            "1c9d9edb-cf0d-41d1-bf6a-df73c273fe32",
            {
                name: "ddccare-api-workshop",
                type: "API",
                param: {
                    path: "/get-user",
                }
            },
            "GET",
            {}, // ไม่มี environment, public_ip
        )

        //Case 6: Test API ddccare-api-workshop มีการกำหนด Web ที่สามารถเข้าถึง
        const policyRequest6 = policySimulator.createPolicyRequest(
            "1c9d9edb-cf0d-41d1-bf6a-df73c273fe32",
            {
                name: "ddccare-api-workshop",
                type: "API",
                param: {
                    path: "/get-user",
                }
            },
            "GET",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
                // timestamp: '2026-06-13 09:10:00',
            },
            "193.100.2.1"
        )

        //Case 6.1: Test API ddccare-api-workshop มีการกำหนด Web ที่สามารถเข้าถึง
        const policyRequest6_1 = policySimulator.createPolicyRequest(
            "1c9d9edb-cf0d-41d1-bf6a-df73c273fe32",
            {
                name: "ddccare-api-workshop",
                type: "API",
                param: {
                    path: "/get-user",
                }
            },
            "GET",
            {
                name: "trb-watch-local",
                type: "WEB",
                timestamp: '2026-06-13 09:10:00',
            },
            // "192.168.0.12"
        )

        //Case 7: Test DATA drg-1-1 ไม่มีเงื่อนไข
        const policyRequest7 = policySimulator.createPolicyRequest(
            "34c54747-e55e-4815-8eb0-82d5f74aebb0",
            {
                name: "drg-1-1",
                type: "DATA",
            },
            "SELECT",
            {},
        )

        const decision = await policySimulator.simulate(policyRequest3, profile.attributes)
        // console.log(decision)
    }

    const runSimulatePolicy = async (profile) => {
        // console.log('Rows', rows)
        const policyFile = JSON.parse(localStorage.getItem('policy-file'));
        if (!policyFile) return;
        const rules = await getRuleByPolicyDiagram(policyFile);
        const mappingRules = rules.reduce((acc, item) => {
            acc[item.resource.name] = {
                type: item.resource.type,
                rule_id: item.rule_id
            };
            return acc;
        }, {});
        // console.log("mappingRules", mappingRules)
        const rowRequest = await Promise.all(
            rows.map(async (row) => {
                if (row.kind !== 'group' && row.checked !== true) {
                    if (row.result) {
                        return {
                            ...row,
                            result: "",
                        };
                    }
                    return row;
                }

                if (row.kind !== 'group' && row.checked === true) {
                    let rule_id = "";

                    if (
                        mappingRules[row.resource] &&
                        (row?.type?.toLowerCase() === mappingRules[row.resource].type.toLowerCase())
                    ) {
                        rule_id = mappingRules[row.resource].rule_id;
                    }

                    // console.log("rule_id", rule_id)

                    let policyRequest;

                    if (row.type === 'WEB') {
                        policyRequest = policySimulator.createPolicyRequest(
                            rule_id,
                            {
                                name: row.resource,
                                type: row.type,
                            },
                            row.action || undefined,
                            {
                                timestamp: row.date_time || undefined,
                            },
                            row.ip_address || undefined
                        );
                    } else if (row.type === 'API') {
                        policyRequest = policySimulator.createPolicyRequest(
                            rule_id,
                            {
                                name: row.resource,
                                type: row.type,
                                param: { path: row.param },
                            },
                            row.action || undefined,
                            {
                                name: row.environment,
                                type: row.environment_type,
                                timestamp: row.date_time || undefined,
                            },
                            row.ip_address || undefined
                        );
                    } else if (row.type === 'DATA') {
                        policyRequest = policySimulator.createPolicyRequest(
                            rule_id,
                            {
                                name: row.resource,
                                type: row.type,
                            },
                            row.action || undefined,
                            {
                                name: row.environment,
                                type: row.environment_type,
                                timestamp: row.date_time || undefined,
                            },
                            row.ip_address || undefined
                        );
                    }

                    if (policyRequest) {
                        const result = await policySimulator.simulate(
                            policyRequest,
                            profile?.attributes
                        );

                        // console.log('decision', result)

                        return {
                            ...row,
                            result: result.decision,
                        };
                    }
                }

                return row;
            })
        );
        setRows(rowRequest);
    };

    const onProfileChanged = (value) => {
        setIsProfileChanged(value)
    };

    const onSelectedProfile = async (profile) => {
        return;
    }

    const onSaveProfile = async (profile) => {
        try {
            const profiles = simulatorProfiles.map(pf => {
                if (pf.id === profile.id) {
                    const updatedAttr = { ...pf.attributes, ...profile.attributes };
                    const updatedProfile = {
                        ...pf,
                        name: profile.name,
                        attributes: updatedAttr,
                        updated_at: profile.updated_at
                    }
                    onSelectedProfile(updatedProfile);
                    return updatedProfile
                }
                return pf;
            });
            // console.log("profiles:", profiles)
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    profiles: profiles
                })
            })
            if (response.ok) {
                // console.log("saved profile.", response)
                dispatch(setSimulatorProfiles(sortedProfilesAttributes(profiles)));
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Error save profile: ", error);
            dispatch(setSnackbarMessage({ message: 'Failed to saved profile.', status: 'error' }));
        }
    };

    const handleEditProfile = (profile) => {
        const selectedProfile = simulatorProfiles.find(pf => pf.id === profile.id);
        setSelectedEditProfile(selectedProfile);
        setOpenManagement(true);
    };

    const handleDeleteProfile = (profile) => {
        setSelectedDeleteProfile(profile);
        setOpenDeleteConfirm(true);
    };

    const createCloneProfileName = (originalName) => {
        let counter = 1;
        let newName = `${originalName}-copy${counter}`;

        while (simulatorProfiles.some(profile => profile.name === newName)) {
            counter++;
            newName = `${originalName}-copy${counter}`;
        }

        return newName;
    };

    const onCloneProfile = async (profile) => {
        const attributes = simulatorProfiles.find(pf => pf.id === profile.id).attributes || {};

        const newProfile = {
            id: uuidv4(),
            name: createCloneProfileName(profile.name),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            attributes: attributes,
        };

        await handleAddProfile(newProfile);
    };

    const handleAddProfile = async (newProfile) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(newProfile)
            });

            if (response.ok) {
                const result = await response.json();
                const updatedProfiles = [...simulatorProfiles, newProfile];
                dispatch(setSimulatorProfiles(sortedProfilesAttributes(updatedProfiles) || []));
                setSelectedEditProfile(newProfile);
                return;
            } else {
                throw new Error("Failed to add simulator profile");
            }
        } catch (error) {
            console.error(error.message);
            dispatch(setSnackbarMessage({
                open: true,
                message: error.message,
                status: "error",
            }));
        }
    };

    const createNewProfileName = () => {
        const hasUnnamed = simulatorProfiles.some(profile => profile.name === "unname");
        if (!hasUnnamed) {
            return "unname";
        }

        let counter = 1;
        let newName = `unname-${counter}`;

        while (simulatorProfiles.some(profile => profile.name === newName)) {
            counter++;
            newName = `unname-${counter}`;
        }

        return newName;
    };

    const onAddNewProfile = async () => {
        let usedAttributes = {};
        for (const attr of subjectSchema) {
            const is_type = dataTypes.find(dt => dt.similar_type.includes(attr.attributeType));
            if (is_type.type === 'boolean') {
                usedAttributes[attr.name] = false;
            } else if (is_type.type === 'array') {
                usedAttributes[attr.name] = [];
            } else {
                usedAttributes[attr.name] = null;
            }
        }

        const newProfile = {
            id: uuidv4(),
            name: createNewProfileName(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            attributes: usedAttributes,
        };

        await handleAddProfile(newProfile);
    };

    const memoizedAttributes = useMemo(() => {
        if (!nodes || nodes.length === 0) return;
        return findSubjectAttrToSimulator(nodes);
    }, [nodes]);

    const handleDelete = async () => {
        try {
            setLoading(true);
            let options = {
                method: 'DELETE',
                headers: {
                    'Content-type': 'application/json',
                },
                body: JSON.stringify({
                    profile_ids: [selectedDeleteProfile.id]
                })
            }
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, options);
            if (response.ok) {
                const updatedProfiles = simulatorProfiles.filter(profile => profile.id !== selectedDeleteProfile.id);
                const notHaveSelected = !updatedProfiles.find(profile => profile.id === selectedProfileSimulate?.id);
                if (notHaveSelected) {
                    dispatch(setSelectedProfileSimulate(null));
                }
                dispatch(setSimulatorProfiles(updatedProfiles));
                setLoading(false);
                setOpenDeleteConfirm(false);
            } else {
                throw response;
            }
        } catch (error) {
            setLoading(false);
            setOpenDeleteConfirm(false);
            console.error("Error save profile: ", error);
            dispatch(setSnackbarMessage({ message: 'Failed to saved profile.', status: 'error' }));
        }

    };

    const onDeleteClose = () => {
        setOpenDeleteConfirm(false);
        setLoading(false);
    };

    const handleOpenProfileManagement = () => {
        setSelectedEditProfile(null);
        setOpenManagement(true);
    };

    const getUsableIp = (ip_address) => {
        if (!ip_address) return null;

        // 1. เอาค่าแรกสุด
        const firstIp = ip_address.split(",")[0].trim();

        // 2. ตรวจประเภท

        // RANGE (เช่น 1.1.1.1-1.1.1.10)
        if (firstIp.includes("-")) {
            const [start] = firstIp.split("-");
            return start.trim();
        }

        // CIDR (เช่น 192.168.1.0/24)
        if (firstIp.includes("/")) {
            const [network, prefix] = firstIp.split("/");
            const baseIp = network.split(".").map(Number);

            // แปลงเป็น usable IP (เอา +1 จาก network)
            if (prefix === "32") return network; // usable อยู่แล้ว

            baseIp[3] += 1; // เพิ่ม host ตัวแรก
            return baseIp.join(".");
        }

        // WILDCARD (เช่น 192.168.1.*)
        if (firstIp.includes("*")) {
            const parts = firstIp.split(".");

            return parts
                .map((p, i) => {
                    if (p === "*") {
                        return i === 3 ? "1" : "0"; // ปกติให้ host เป็น 1
                    }
                    return p;
                })
                .join(".");
        }

        return firstIp;
    }

    const getDefaultDateTime = (timeCondition) => {
        const now = dayjs();

        if (!timeCondition || typeof timeCondition !== 'object') {
            return now.format("YYYY-MM-DD HH:mm:ss");
        }

        const getValidSlotsByDay = (dayKey) => {
            const slots = timeCondition?.[dayKey];
            if (!Array.isArray(slots)) return [];
            return slots.filter((slot) => String(slot?.start || '').trim());
        };

        const buildDateTimeFromStart = (baseDate, start) => {
            const timeParts = String(start).split(':').map(Number);
            const hour = Number.isFinite(timeParts[0]) ? timeParts[0] : 0;
            const minute = Number.isFinite(timeParts[1]) ? timeParts[1] : 0;
            const second = Number.isFinite(timeParts[2]) ? timeParts[2] : 0;

            return baseDate
                .hour(hour)
                .minute(minute)
                .second(second)
                .millisecond(0)
                .format("YYYY-MM-DD HH:mm:ss");
        };

        const todayKey = now.format('dddd').toLowerCase();
        const todaySlots = getValidSlotsByDay(todayKey);
        if (todaySlots.length > 0) {
            return buildDateTimeFromStart(now, todaySlots[0].start);
        }

        for (let i = 1; i <= 14; i += 1) {
            const previousDay = now.subtract(i, 'day');
            const previousKey = previousDay.format('dddd').toLowerCase();
            const previousSlots = getValidSlotsByDay(previousKey);
            if (previousSlots.length > 0) {
                return buildDateTimeFromStart(previousDay, previousSlots[0].start);
            }
        }

        return now.format("YYYY-MM-DD HH:mm:ss");
    }

    const initialRequest = (rules) => {
        // Rule Web
        let webRequest = [
            {
                id: uuidv4(),
                kind: "group",
                checked: false,
                resource: "Applications"
            }
        ];
        for (const r of rules) {
            if (r?.resource?.type === 'WEB' || r?.resource?.type === 'NATIVE') {
                let request = {
                    id: uuidv4(),
                    kind: "item",
                    checked: false,
                    resource: r.resource.name,
                    type: r?.resource?.type,
                    action: r.action[0],
                    result: "",
                    is_default_item: true,
                };
                if (r?.environment?.ip_address) {
                    request['ip_address'] = getUsableIp(r?.environment?.ip_address);
                }
                if (Object.keys(r?.environment?.time).length > 0) {
                    request['date_time'] = getDefaultDateTime(r?.environment?.time);
                }
                webRequest.push(request);
            }
        }

        //Rule API
        let apiRequest = [
            {
                id: uuidv4(),
                kind: "group",
                checked: false,
                resource: "RESTful-API"
            }
        ];
        for (const r of rules) {
            if (r?.resource?.type === 'API') {
                if (r?.environment?.resources.length === 0) {
                    let request = {
                        id: uuidv4(),
                        kind: "item",
                        checked: false,
                        resource: r.resource.name,
                        type: r?.resource?.type,
                        action: r.action[0],
                        param: r.resource.param.path,
                        environment: "",
                        environment_type: "",
                        result: "",
                        is_default_item: true,
                    };
                    if (r?.environment?.ip_address) {
                        request['ip_address'] = getUsableIp(r?.environment?.ip_address)
                    }
                    if (Object.keys(r?.environment?.time).length > 0) {
                        request['date_time'] = getDefaultDateTime(r?.environment?.time);
                    }
                    apiRequest.push(request);
                } else {
                    const resources = r.environment.resources;
                    for (let i = 0; i < resources.length; i++) {
                        let request = {
                            id: uuidv4(),
                            kind: "item",
                            checked: false,
                            resource: r.resource.name,
                            type: r?.resource?.type,
                            action: r.action[0],
                            param: r.resource.param.path,
                            environment: resources[i].name,
                            environment_type: resources[i].type,
                            result: "",
                            is_default_item: true,
                        };
                        if (r?.environment?.ip_address) {
                            request['ip_address'] = getUsableIp(r?.environment?.ip_address)
                        }
                        if (Object.keys(r?.environment?.time).length > 0) {
                            request['date_time'] = getDefaultDateTime(r?.environment?.time);
                        }
                        apiRequest.push(request);
                    }
                }
            }
        }

        //Rule Data
        let dataRequest = [
            {
                id: uuidv4(),
                kind: "group",
                checked: false,
                resource: "Data"
            }
        ];
        for (const r of rules) {
            if (r?.resource?.type === 'DATA') {
                if (r?.environment?.resources.length === 0) {
                    let request = {
                        id: uuidv4(),
                        kind: "item",
                        checked: false,
                        resource: r.resource.name,
                        type: r?.resource?.type,
                        action: r.action[0],
                        // param: r.resource.param,
                        environment: "",
                        environment_type: "",
                        result: "",
                        is_default_item: true,
                    };
                    if (r?.environment?.ip_address) {
                        request['ip_address'] = getUsableIp(r?.environment?.ip_address)
                    }
                    if (Object.keys(r?.environment?.time).length > 0) {
                        request['date_time'] = getDefaultDateTime(r?.environment?.time);
                    }
                    dataRequest.push(request);
                } else {
                    const resources = r.environment.resources;
                    for (let i = 0; i < resources.length; i++) {
                        let request = {
                            id: uuidv4(),
                            kind: "item",
                            checked: false,
                            resource: r.resource.name,
                            type: r?.resource?.type,
                            action: r.action[0],
                            // param: r.resource.param,
                            environment: resources[i].name,
                            environment_type: resources[i].type,
                            result: "",
                            is_default_item: true,
                        };
                        if (r?.environment?.ip_address) {
                            request['ip_address'] = getUsableIp(r?.environment?.ip_address)
                        }
                        if (Object.keys(r?.environment?.time).length > 0) {
                            request['date_time'] = getDefaultDateTime(r?.environment?.time);
                        }
                        dataRequest.push(request);
                    }
                }
            }
        }

        //Merge initial request
        const result = [...webRequest, ...apiRequest, ...dataRequest]
        return result;
    };

    const buildRulesSnapshot = (rules = []) => {
        if (!Array.isArray(rules)) return [];

        const snapshot = rules.map((rule) => {
            const resourceType = String(rule?.resource?.type || '').toUpperCase();
            const envResources = Array.isArray(rule?.environment?.resources)
                ? rule.environment.resources.map((res) => ({
                    name: String(res?.name || ''),
                    type: String(res?.type || ''),
                }))
                : [];

            envResources.sort((a, b) => `${a.name}:${a.type}`.localeCompare(`${b.name}:${b.type}`));

            return {
                resource_name: String(rule?.resource?.name || ''),
                resource_type: resourceType,
                action: Array.isArray(rule?.action) ? [...rule.action] : [],
                param_path: String(rule?.resource?.param?.path || ''),
                environment_ip_address: String(rule?.environment?.ip_address || ''),
                environment_time: rule?.environment?.time && typeof rule.environment.time === 'object'
                    ? JSON.stringify(rule.environment.time)
                    : '',
                environment_resources: envResources,
            };
        });

        snapshot.sort((a, b) => `${a.resource_name}:${a.resource_type}:${a.param_path}`.localeCompare(`${b.resource_name}:${b.resource_type}:${b.param_path}`));
        return snapshot;
    };

    const isRulesSnapshotEqual = (left = [], right = []) => {
        return JSON.stringify(left || []) === JSON.stringify(right || []);
    };

    const hasMatchingRule = (row, rules = []) => {
        if (!row || row.kind !== 'item') return false;

        return rules.some((rule) =>
            String(rule?.resource?.name || '') === String(row?.resource || '') &&
            String(rule?.resource?.type || '').toUpperCase() === String(row?.type || '').toUpperCase()
        );
    };

    const mergeRowsWhenRulesChanged = (existingRows = [], rules = []) => {
        const freshRows = initialRequest(rules);
        const safeExistingRows = Array.isArray(existingRows) ? existingRows : [];

        const customRowsByType = { WEB: [], API: [], DATA: [] };
        safeExistingRows.forEach((row) => {
            if (row?.kind !== 'item' || row?.is_default_item) return;
            if (!hasMatchingRule(row, rules)) return;

            const rowType = String(row?.type || '').toUpperCase();
            if (rowType === 'API') {
                customRowsByType.API.push(row);
            } else if (rowType === 'DATA') {
                customRowsByType.DATA.push(row);
            } else {
                customRowsByType.WEB.push(row);
            }
        });

        const groupedFresh = {
            WEB: freshRows.filter((row) => row.kind === 'item' && (row.type === 'WEB' || row.type === 'NATIVE')),
            API: freshRows.filter((row) => row.kind === 'item' && row.type === 'API'),
            DATA: freshRows.filter((row) => row.kind === 'item' && row.type === 'DATA'),
        };

        return [
            { id: uuidv4(), kind: 'group', checked: false, resource: 'Applications' },
            ...groupedFresh.WEB,
            ...customRowsByType.WEB,
            { id: uuidv4(), kind: 'group', checked: false, resource: 'RESTful-API' },
            ...groupedFresh.API,
            ...customRowsByType.API,
            { id: uuidv4(), kind: 'group', checked: false, resource: 'Data' },
            ...groupedFresh.DATA,
            ...customRowsByType.DATA,
        ];
    };

    const handleCheckInitial = async () => {
        const policyRequest = JSON.parse(localStorage.getItem('policy-simulator'));
        const policyFile = JSON.parse(localStorage.getItem('policy-file'));
        const policyDiagramId = policyFile?.policy_diagram_id;
        const profileId = selectedProfileSimulate?.id;

        if (!policyFile || !policyDiagramId || !profileId) return;

        const currentRequest =
            policyRequest && typeof policyRequest === 'object' ? { ...policyRequest } : {};
        const rawDiagramStore = currentRequest[policyDiagramId];

        const rules = await getRuleByPolicyDiagram(policyFile);
        const latestRulesSnapshot = buildRulesSnapshot(rules);

        let diagramStore = {};
        if (rawDiagramStore && !Array.isArray(rawDiagramStore) && typeof rawDiagramStore === 'object') {
            diagramStore = { ...rawDiagramStore };
        }

        // Backward compatibility: migrate old structure { [diagramId]: rows[] } to profile-level storage.
        if (Array.isArray(rawDiagramStore)) {
            diagramStore[profileId] = {
                rows: rawDiagramStore,
                rules_snapshot: latestRulesSnapshot,
            };
        }

        const resetRowResults = (sourceRows = []) => {
            if (!Array.isArray(sourceRows)) return [];
            return sourceRows.map((row) => {
                if (row?.kind !== 'item') return row;
                return {
                    ...row,
                    result: "",
                };
            });
        };

        const profileStoreRaw = diagramStore[profileId];
        let profileStore = null;

        if (Array.isArray(profileStoreRaw)) {
            profileStore = {
                rows: profileStoreRaw,
                rules_snapshot: [],
            };
        } else if (profileStoreRaw && typeof profileStoreRaw === 'object') {
            profileStore = {
                rows: Array.isArray(profileStoreRaw.rows) ? profileStoreRaw.rows : [],
                rules_snapshot: Array.isArray(profileStoreRaw.rules_snapshot) ? profileStoreRaw.rules_snapshot : [],
            };
        }

        if (!profileStore) {
            const initialRows = resetRowResults(initialRequest(rules));
            diagramStore[profileId] = {
                rows: initialRows,
                rules_snapshot: latestRulesSnapshot,
            };
            currentRequest[policyDiagramId] = diagramStore;

            setRows(initialRows);
            localStorage.setItem('policy-simulator', JSON.stringify(currentRequest));
            return;
        }

        const rulesChanged = !isRulesSnapshotEqual(profileStore.rules_snapshot, latestRulesSnapshot);

        if (!rulesChanged) {
            const rowsWithResetResult = resetRowResults(profileStore.rows);
            setRows(rowsWithResetResult);
            diagramStore[profileId] = {
                rows: rowsWithResetResult,
                rules_snapshot: latestRulesSnapshot,
            };
            currentRequest[policyDiagramId] = diagramStore;
            localStorage.setItem('policy-simulator', JSON.stringify(currentRequest));
            return;
        }

        const mergedRows = resetRowResults(mergeRowsWhenRulesChanged(profileStore.rows, rules));
        diagramStore[profileId] = {
            rows: mergedRows,
            rules_snapshot: latestRulesSnapshot,
        };
        currentRequest[policyDiagramId] = diagramStore;

        setRows(mergedRows);
        localStorage.setItem('policy-simulator', JSON.stringify(currentRequest));
    };

    useEffect(() => {
        if (openSimulatorPolicy && selectedProfileSimulate) {
            handleCheckInitial();
        }
    }, [openSimulatorPolicy, selectedProfileSimulate])

    return (
        <>
            <Panel direction="vertical" defaultSize={40}>
                <PanelGroup direction="horizontal" id="simulator-panel-group">
                    <Panel id="profile-panel" order={1} defaultSize={20} minSize={20} className="flex flex-col min-w-0">
                        <AppBar position="static" sx={{ height: "30px", boxShadow: 'none', backgroundColor: "whitesmoke" }}>
                            <Toolbar disableGutters variant="dense" sx={{ minHeight: '30px', height: '30px', marginLeft: '12px', marginRight: '10px', display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant='inherit' color='textSecondary'>User Profile</Typography>
                                <Tooltip title="Profile Management" arrow>
                                    <TuneIcon className='text-gray-400 p-0.5 hover:text-blue-600 cursor-pointer' onClick={handleOpenProfileManagement} />
                                </Tooltip>
                            </Toolbar>
                        </AppBar>
                        <Box className="flex-1 w-full overflow-auto bg-white border-collapse">
                            <CustomProfilesPolicySimulator
                                profiles={profiles}
                                onProfileChanged={(isModified) => onProfileChanged(isModified)}
                                onSelectedProfile={(profile) => onSelectedProfile(profile)}
                                onSaveProfile={(profile) => onSaveProfile(profile)}
                                handleEditProfile={(profile) => handleEditProfile(profile)}
                                handleDeleteProfile={(profile) => handleDeleteProfile(profile)}
                                nodes={nodes}
                                runSimulatePolicy={(profile) => runSimulatePolicy(profile)}
                            />
                        </Box>
                    </Panel>
                    <PanelResizeHandle className='w-0.5 bg-slate-300 hover:bg-slate-400' />
                    <Panel
                        id="policy-request-panel"
                        order={2}
                        style={{
                            // display: 'flex',
                            // flexDirection: 'column',
                            // minWidth: 0
                            overflow: 'hidden',
                            width: 100
                        }}
                    >
                        <AppBar position="static" sx={{ height: "30px", width: "100%", boxShadow: 'none', backgroundColor: "whitesmoke", flexShrink: 0 }}>
                            <Toolbar disableGutters variant="dense" sx={{ minHeight: '30px', height: '30px', marginLeft: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                {/* <div className={`flex justify-center items-center pl-2 rounded-full bg-green-600 cursor-pointer'`} >
                                    <Typography variant='button' fontSize={12} className='text-white'>
                                        Run
                                    </Typography>
                                    <PlayCircleFilledWhiteIcon
                                        className={`p-0.5`}
                                        onClick={() => runSimulatePolicy()}
                                    />
                                </div> */}
                                <div></div>
                                <IconButton
                                    aria-label="close console"
                                    aria-controls="close-console"
                                    onClick={onClose}
                                    color="default"
                                    style={{
                                        height: '25px',
                                        width: '25px',
                                        marginRight: '4px'
                                    }}
                                >
                                    <CloseIcon className='p-0.5' />
                                </IconButton>
                            </Toolbar>
                        </AppBar>
                        <Box
                            className="h-[calc(100%-30px)] bg-white border-collapse"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box sx={{ flex: 1, height: 0, width: '100%' }}>
                                {selectedProfileSimulate ? (
                                    <DataGridPolicySimulator
                                        rows={rows}
                                        setRows={setRows}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: 2,
                                        }}
                                    >
                                        <Typography fontSize={13} color="text.secondary" textAlign="center">
                                            Please select profile before loading policy request.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Panel>
                </PanelGroup>
            </Panel>
            <UserProfileManagement
                open={openManagement}
                setOpen={setOpenManagement}
                project={project}
                profiles={simulatorProfiles}
                onSaveProfile={(profile) => onSaveProfile(profile)}
                usedSubjectAttributes={memoizedAttributes}
                onAddNewProfile={onAddNewProfile}
                onCloneProfile={(profile) => onCloneProfile(profile)}
                selectedEditProfile={selectedEditProfile}
                initialProfileId={selectedEditProfile?.id}
            />
            <ConfirmDialogReuse
                open={openDeleteConfirm}
                maxWidth={"sm"}
                content={
                    <div>
                        <Typography variant="subtitle1">Are you sure you want to delete profile "{selectedDeleteProfile?.name}" ?</Typography>
                    </div>
                }
                actions={
                    <div className='grid grid-cols-2 gap-2'>
                        <LoadingButton
                            variant="contained"
                            loading={loading}
                            onClick={() => handleDelete()}
                        >
                            Confirm
                        </LoadingButton>
                        <Button
                            disabled={loading}
                            variant="outlined"
                            onClick={() => onDeleteClose()}
                        >
                            Cancel
                        </Button>
                    </div>
                }
            />
        </>
    );
};

export default PolicySimulatorPanel;